import moment from "moment";
import {
  SeverityScore,
  parseCVSSVector,
  blankSeverityScore
} from "../../../../components/forms/inputs/SeverityScoreSelector";
import { ReportState, ReportStatus, ReportUpdateFields } from "./reports";
import { BaseItem } from "../../axios";
import { DATE_FORMAT } from "../../../..";
import { createZip } from "../../../zipAttachments";
import { TriageStatus } from "../usersApi";
import { ProgramCreator, ProgramTarget } from "../programs/parsePrograms";

type ReportCreator = {
  id?: number;
  displayName: string;
  pfp?: string;
  username?: string; // Added for username-based navigation
};

export enum ReportRejectReason {
  DUPLICATE = "Duplicate",
  OUT_OF_SCOPE = "Out Of Scope",
  DISCLOSED = "Disclosed",
  INFORMATIVE = "Informative",
  SPAM = "Spam",
  NONE = ""
}

// The report object that is returned by the API
export type CTBReportAPIResponse = {
  report_id: number;
  user_id: number;
  program_id: number;
  is_delete: boolean;
  is_archived?: boolean;
  report_title: string;
  scope?: string;
  category?: string;
  severity?: string;
  severity_category?: string;
  severity_score?: string | number;
  cvss_version?: "3.1" | "4.0" | string;
  cvss_vector?: string;
  cvss_score?: number;
  description?: string;
  instructions?: string;
  impact?: string;
  fix?: string;
  additional_info?: string;
  jira_issue_key?: string;
  submitted_date?: string;
  state?: ReportState;
  payout?: number;
  triage_status?: TriageStatus;
  comments?: string;
  attachments?: string;
  reject_reason?: ReportRejectReason;
  certificate_generated?: boolean;
  read?: boolean;
  creator?: ReportCreator;
  program_creator?: ProgramCreator;
  retest_status?: string;
  run_id?: number;
  asset_id?: string;
  target_ids?: string[];
  endpoint_ids?: number[];
  asset_name?: string;
  target_values?: string[];
  endpoint_values?: string[];
  target_endpoint_map?: Record<string, string[]>;
  target_metadata?: Record<string, any>;
};

export type CTBReport = {
  title: string;
  programId: number;
  scope?: string;
  category?: string;
  severity?: SeverityScore;
  severityCategory?: string;
  assetType?: string;
  description?: string;
  instructions?: string;
  impact?: string;
  fix?: string;
  additionalInfo?: string;
  jiraIssueKey?: string;
  attachments?: string;
  payout?: number;
  comments?: string;
  rejectReason?: ReportRejectReason;
  certificateGenerated: boolean;
  submittedDate?: string;
  state?: ReportState;
  triageStatus?: TriageStatus;
  status?: ReportStatus;
  adminState?: TriageStatus;
  userId: number;
  isDelete: boolean;
  isArchived: boolean;
  read?: boolean;
  creator?: ReportCreator;
  program_creator?: ProgramCreator;
  retestStatus?: string;
  runId?: number;
  assetId?: string;
  targetIds?: string[];
  endpointIds?: number[];
  assetName?: string;
  targetValues?: string[];
  endpointValues?: string[];
  targetEndpointMap?: Record<string, string[]>;
  targetMetadata?: Record<string, any>;
} & BaseItem;

/**
 * Extract and parse each of the expected fields from
 * the reports API response
 */
export const parseReport = (report: CTBReportAPIResponse) =>
  ({
    id: report.report_id,
    userId: report.user_id,
    programId: report.program_id,
    isDelete: report.is_delete,
    isArchived: !!report.is_archived,
    title: report.report_title,
    submittedDate: report.submitted_date
      ? moment(report.submitted_date).format(DATE_FORMAT)
      : undefined,
    status: report.is_delete
      ? ReportStatus.DELETED
      : report.state !== null
        ? ReportStatus.SUBMITTED
        : report.state === null
          ? ReportStatus.DRAFT
          : undefined,
    additionalInfo: report.additional_info,
    jiraIssueKey: report.jira_issue_key,
    triageStatus: report.triage_status,
    certificateGenerated: report.certificate_generated,
    severityCategory: (() => {
      if (!report.severity || report.severity.toLowerCase().includes("undefined") || report.severity.trim() === "") {
        return "Draft";
      }
      if (report.cvss_version === "4.0" || report.severity.startsWith("CVSS:4.0")) {
        return report.severity_category || "Low";
      }
      const parsed = parseCVSSVector(report.severity);
      if (parsed && parsed.category && parsed.category !== "None") {
        return parsed.category;
      }
      return report.severity_category || "Low";
    })(),
    severity: (() => {
      if (report.cvss_version === "4.0" || (report.severity && report.severity.startsWith("CVSS:4.0"))) {
        const rawScore = report.cvss_score ?? (report.severity_score !== undefined ? parseFloat(String(report.severity_score)) : undefined);
        return {
          category: report.severity_category || "None",
          score: rawScore !== undefined && !isNaN(rawScore) ? rawScore.toFixed(1) : "-.-",
          cvss_version: "4.0" as const,
          cvss_vector: report.cvss_vector || report.severity,
          cvss_score: rawScore,
          values: {
            AV: undefined,
            AC: undefined,
            PR: undefined,
            UI: undefined,
            S: undefined,
            C: undefined,
            I: undefined,
            A: undefined
          }
        };
      }
      const parsed = parseCVSSVector(report.severity ?? "");
      if (!report.severity || report.severity.toLowerCase().includes("undefined") || report.severity.trim() === "") {
        return {
          category: "Draft",
          score: "-.-",
          cvss_version: (report.cvss_version as any) || "3.1",
          cvss_vector: report.cvss_vector,
          cvss_score: report.cvss_score,
          values: parsed ? parsed.values : {
            AV: undefined,
            AC: undefined,
            PR: undefined,
            UI: undefined,
            S: undefined,
            C: undefined,
            I: undefined,
            A: undefined
          }
        };
      }
      return {
        ...parsed,
        cvss_version: (report.cvss_version as any) || "3.1",
        cvss_vector: report.cvss_vector || report.severity,
        cvss_score: report.cvss_score ?? (parsed && parsed.score !== "-.-" ? parseFloat(parsed.score) : undefined)
      };
    })(),
    rejectReason: report.reject_reason,
    scope: report.scope,
    category: report.category,
    description: report.description,
    instructions: report.instructions,
    impact: report.impact,
    fix: report.fix,
    state: report.state,
    payout: report.payout,
    comments: report.comments,
    attachments: report.attachments,
    severityVector: report.severity,
    read: report.read,
    creator: report.creator,
    program_creator: report.program_creator,
    retestStatus: report.retest_status,
    runId: report.run_id,
    assetId: report.asset_id,
    targetIds: report.target_ids,
    endpointIds: report.endpoint_ids,
    assetName: report.asset_name,
    targetValues: report.target_values,
    endpointValues: report.endpoint_values,
    targetEndpointMap: report.target_endpoint_map,
    targetMetadata: report.target_metadata
  }) as CTBReport;

/**
 * Convert report update fields into form data
 * to be used in an API request
 */
export const prepareReportFormData = async ({
  id,
  details,
  files
}: {
  id?: number;
  details: ReportUpdateFields;
  files?: Blob[];
}) => {
  const isCvss40 = details.severity?.cvss_version === "4.0" || details.severity?.cvss_vector?.startsWith("CVSS:4.0");

  const severityValues = details.severity?.values;
  const cvssVector = isCvss40
    ? details.severity?.cvss_vector
    : severityValues
      ? `CVSS:3.1/AV:${severityValues.AV}/AC:${severityValues.AC}/PR:${severityValues.PR}/UI:${severityValues.UI}/S:${severityValues.S}/C:${severityValues.C}/I:${severityValues.I}/A:${severityValues.A}`
      : details.severity?.cvss_vector;

  const isV2 = !!details.assetId || (details.targetIds && details.targetIds.length > 0) || !!details.runId;

  const parsedScore = details.severity?.score && details.severity.score !== "-.-" ? parseFloat(details.severity.score) : undefined;

  const body = {
    report_id: id,
    program_id: details.programId,
    report_title: details.title,
    scope: isV2 ? undefined : details.scope,
    category: details.category,
    severity: cvssVector,
    severity_score: parsedScore,
    cvss_version: isCvss40 ? "4.0" : "3.1",
    cvss_vector: cvssVector,
    cvss_score: isCvss40 ? (details.severity?.cvss_score ?? parsedScore) : parsedScore,
    description: details.description,
    instructions: details.instructions,
    impact: details.impact,
    fix: details.fix,
    additional_info: details.additionalInfo,
    run_id: details.runId,
    asset_id: details.assetId,
    target_ids: details.targetIds,
    endpoint_ids: details.endpointIds,
    isDraft: details.isDraft
  };

  // Add the body to some Form Data
  const formData = new FormData();
  formData.append("data", JSON.stringify(body));

  // Compress the attachment to send
  if (files) {
    const zippedFiles = await createZip(files);
    if (zippedFiles !== null) formData.append("attachments", zippedFiles);
  }

  return formData;
};

export type ReportCommentAPIresponse = {
  comment_id: number;
  user_id: number;
  report_id: number;
  comment: string;
  createdAt: string;
  updatedAt: string;
  pfp: string;
  username: string;
  role?: number; // Add role information for each commenter
};

export type ReportRecommendation = {
  report_id: number;
  report_title: string;
  severity_category: string;
  submitted_date: string;
};

/**
 * Derive asset type from report scope/targets and program targets
 * Matches the report's scope or target values with targetName in program targets
 * Returns the corresponding targetType as asset type
 */
export const deriveAssetType = (
  scope?: string,
  programTargets?: ProgramTarget[],
  targetValues?: string[]
): string | undefined => {
  if (!programTargets || programTargets.length === 0) {
    return undefined;
  }

  // 1. Try matching by scope string
  if (scope) {
    const matchingTarget = programTargets.find(target =>
      target.targetName && scope.toLowerCase().includes(target.targetName.toLowerCase())
    );
    if (matchingTarget) return matchingTarget.targetType;
  }

  // 2. Try matching by target values from report
  if (targetValues && targetValues.length > 0) {
    for (const val of targetValues) {
      const matchingTarget = programTargets.find(target =>
        target.targetName && val.toLowerCase().includes(target.targetName.toLowerCase())
      );
      if (matchingTarget) return matchingTarget.targetType;
    }
  }

  // 3. Fallback: If there's only one target type in the program, use it
  const uniqueTypes = new Set(programTargets.map(t => t.targetType).filter(Boolean));
  if (uniqueTypes.size === 1) {
    return Array.from(uniqueTypes)[0];
  }

  return undefined;
};
